import { ChoiceRow, Radio, RadioGroup, formatPhone } from '@westhetics/ui'
import { useId } from 'react'
import type { Channels, SignatureProvider } from './api'
import { CHANNEL_LABELS, PROVIDER_CHANNEL_NOTES, channelBlocker } from './labels'

/**
 * Por onde o link vai (RT-12): WhatsApp · E-mail · Os dois, com o contato da ficha ao lado e o motivo
 * quando um canal não pode. No WhatsApp assistido (P-ASS-21) a linha diz que a clínica manda à mão; na
 * Autentique (RN-ASS-36), diz que é ela quem entrega.
 */
export function ChannelPicker({
  value,
  onChange,
  contact,
  whatsappMode,
  provider = 'westhetics',
  label = 'Enviar por',
}: {
  value: Channels
  onChange: (channels: Channels) => void
  contact: { whatsapp: string | null; email: string | null }
  whatsappMode: 'provider' | 'manual'
  /** Quem conduz a assinatura na clínica ativa; ausente, as telas da Westhetics. */
  provider?: SignatureProvider
  label?: string
}) {
  const id = useId()
  const options: Channels[] = ['whatsapp', 'email', 'both']
  const isAutentique = provider === 'autentique'
  const manualNote =
    !isAutentique && whatsappMode === 'manual' ? ' · você manda a mensagem pelo seu WhatsApp' : ''
  function description(channels: Channels) {
    const blocker = channelBlocker(channels, contact)
    if (blocker) return blocker
    if (isAutentique) {
      const address =
        channels === 'whatsapp'
          ? formatPhone(contact.whatsapp ?? '')
          : channels === 'email'
            ? (contact.email ?? '')
            : null
      return address
        ? `${address} · ${PROVIDER_CHANNEL_NOTES[channels]}`
        : `Pelos dois, com o mesmo link · ${PROVIDER_CHANNEL_NOTES[channels]}`
    }
    if (channels === 'whatsapp') return `${formatPhone(contact.whatsapp ?? '')}${manualNote}`
    if (channels === 'email') return contact.email ?? ''
    return `A mesma mensagem pelos dois, com o mesmo link${manualNote}`
  }
  return (
    <div>
      <div id={`${id}-label`} className="eyebrow">
        {label}
      </div>
      <RadioGroup
        aria-labelledby={`${id}-label`}
        value={value}
        onValueChange={(next) => onChange(next as Channels)}
        className="mt-2 flex flex-col"
      >
        {options.map((channels) => {
          const blocked = channelBlocker(channels, contact) !== null
          return (
            <ChoiceRow
              key={channels}
              htmlFor={`${id}-${channels}`}
              control={<Radio id={`${id}-${channels}`} value={channels} disabled={blocked} />}
              description={description(channels)}
            >
              {CHANNEL_LABELS[channels]}
            </ChoiceRow>
          )
        })}
      </RadioGroup>
    </div>
  )
}
